// ============================================================================
// EMISSION FACTORS API CLIENT
// ============================================================================
// Client-side API calls for emission factors management
// ============================================================================

import { ApiClient } from '../../../core/apiClient';
import {
  EmissionFactor,
  EmissionFactorSource,
  EmissionFactorCategory,
  EmissionFactorSubcategory,
  EmissionFactorCalculation,
  EmissionFactorSearchOptions,
  CreateEmissionFactorRequest,
  UpdateEmissionFactorRequest,
  CalculateEmissionsRequest,
  CalculateEmissionsResponse,
  ApiResponse
} from '@shared/types/emissionFactors';

class EmissionFactorsApi extends ApiClient {
  private baseUrl = '/emission-factors';

  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  // ============================================================================
  // EMISSION FACTOR CRUD OPERATIONS
  // ============================================================================

  async getEmissionFactors(options: EmissionFactorSearchOptions = {}): Promise<EmissionFactor[]> {
    const params = new URLSearchParams();

    if (options.category) params.append('category', options.category);
    if (options.source) params.append('source', options.source);
    if (options.unit) params.append('unit', options.unit);
    if (options.search) params.append('search', options.search);
    if (options.isActive !== undefined) params.append('isActive', options.isActive.toString());

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await this.client.get<ApiResponse<EmissionFactor[]>>(url);
    return response.data?.data || [];
  }

  async getEmissionFactor(id: number): Promise<EmissionFactor | null> {
    try {
      const response = await this.client.get<ApiResponse<EmissionFactor>>(`${this.baseUrl}/${id}`);
      return response.data?.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createEmissionFactor(data: CreateEmissionFactorRequest): Promise<EmissionFactor> {
    const response = await this.client.post<ApiResponse<EmissionFactor>>(this.baseUrl, data);
    if (!response.data?.data) {
      throw new Error('Failed to create emission factor');
    }
    return response.data.data;
  }

  async updateEmissionFactor(id: number, data: UpdateEmissionFactorRequest): Promise<EmissionFactor | null> {
    try {
      const response = await this.client.put<ApiResponse<EmissionFactor>>(`${this.baseUrl}/${id}`, data);
      return response.data?.data || null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async deleteEmissionFactor(id: number): Promise<boolean> {
    try {
      await this.client.delete(`${this.baseUrl}/${id}`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }

  // ============================================================================
  // EMISSION FACTOR SOURCES
  // ============================================================================

  async getEmissionFactorSources(): Promise<EmissionFactorSource[]> {
    const response = await this.client.get<ApiResponse<EmissionFactorSource[]>>(`${this.baseUrl}/sources`);
    return response.data?.data || [];
  }

  // ============================================================================
  // EMISSION FACTOR CATEGORIES
  // ============================================================================

  async getEmissionFactorCategories(): Promise<EmissionFactorCategory[]> {
    const response = await this.client.get<ApiResponse<EmissionFactorCategory[]>>(`${this.baseUrl}/categories`);
    return response.data?.data || [];
  }

  async getEmissionFactorSubcategories(categoryId: string): Promise<EmissionFactorSubcategory[]> {
    const response = await this.client.get<ApiResponse<EmissionFactorSubcategory[]>>(`${this.baseUrl}/categories/${categoryId}/subcategories`);
    return response.data?.data || [];
  }

  // ============================================================================
  // EMISSION CALCULATIONS
  // ============================================================================

  async calculateEmissions(data: CalculateEmissionsRequest): Promise<CalculateEmissionsResponse> {
    const response = await this.client.post<ApiResponse<CalculateEmissionsResponse>>(`${this.baseUrl}/calculate`, data);
    if (!response.data?.data) {
      throw new Error('Failed to calculate emissions');
    }
    return response.data.data;
  }

  async getEmissionCalculations(limit: number = 50, offset: number = 0): Promise<EmissionFactorCalculation[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const response = await this.client.get<ApiResponse<EmissionFactorCalculation[]>>(`${this.baseUrl}/calculations?${params}`);
    return response.data?.data || [];
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  async searchEmissionFactors(query: string): Promise<EmissionFactor[]> {
    const response = await this.client.get<ApiResponse<EmissionFactor[]>>(`${this.baseUrl}/search/${encodeURIComponent(query)}`);
    return response.data?.data || [];
  }

  async getEmissionFactorsByCategory(category: string): Promise<EmissionFactor[]> {
    const response = await this.client.get<ApiResponse<EmissionFactor[]>>(`${this.baseUrl}/category/${category}`);
    return response.data?.data || [];
  }
}

export const emissionFactorsApi = new EmissionFactorsApi();